import { AgTechRendering } from '@agtech/core/components/AgTechRendering';
import { AgTechMessage, IAgTechAppMessaging, isAgTechMessage } from '@agtech/core/functions/AgTechAppMessaging';
import { logEvent } from '@agtech/core/logging/AgTechLogger';
import { isDev } from '@agtech/core/utilities/AgTechUtilities';
import AgTechTitleDetailHeader from '../../Headers/AgTechTitleDetailHeader';
import AgTechIconBadge from '../../Icons/AgTechIconBadge';
import AgTechToast, { AgTechStandardToast, AgTechToastType } from '../../Portals/Toasts/AgTechToast';
import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react'
import AgTechHeaderDetail from '../../Headers/AgTechHeaderDetail';

/// Types

export type AgTechToast = AgTechMessage & {
    icon?: string,
    type?: AgTechToastType
}

export type AgTechToastRendering = {
    id: string,
    toast: AgTechRendering
}

/// Components

export type AgTechToastSurfaceProps = {
    toastManager: AgTechToastManager
}

const AgTechToastSurface = (props: AgTechToastSurfaceProps) => {
    let toastManager = props.toastManager;
    let toasts = toastManager.renderedToasts;
    let surfaceClass = toasts.length > 0 ? 'row-based justify-end !flex' : '';

    return (
        <div id='ToastDrawer' className={`py-6 absolute hidden left-0 bottom-0 w-[600px] z-20 ${surfaceClass}`}>
            {toasts.map(toast => (
                <AgTechRenderedToast
                    key={toast.id}
                    toast={toast}
                    toastManager={toastManager}
                />
            ))}
        </div>
    );
};

declare type AgTechRenderedToastProps = {
    toast: AgTechToastRendering,
    toastManager: AgTechToastManager
}

let AgTechRenderedToast = (props: AgTechRenderedToastProps) => {
    let [isFaded, setIsFaded] = useState(false);
    let [isShown, setIsShown] = useState(true);

    let shownClass = isFaded ? 'faded' : '';

    let removeToast = () => {
        setIsShown(false);
        props.toastManager.removeToast(props.toast.id);
    }
    
    useEffect(() => {
        if (isFaded) {
            setTimeout(removeToast, 500);
        }
    }, [isFaded]);

    useEffect(() => {
       setTimeout(() => {
            if (process.env.NODE_ENV !== 'development') {
                setIsFaded(true);
            }
       }, 5000);
    }, []);

    const showClass = isFaded ? 'animate-[toastFadeOut_500ms_forwards]' : 'animate-[toastFadeIn_500ms_forwards]';

    return isShown ? (
        <div
            className={`relative rounded bg-white mt-2 overflow-hidden flex ${showClass}`}
            onClick={e => e.stopPropagation()}
        >
            <i className="fas fa-times absolute cursor-pointer text-detail text-md top-[20px] right-[20px] color-changing hover:text-primary" onClick={() => setIsFaded(true)} />
            {props.toast.toast.rendering()}
        </div>
    ) : null;
}

/// Utilities

export type AgTechToastManager = IAgTechAppMessaging & {
    renderedToasts: AgTechToastRendering[],
    show: (rendering: AgTechRendering) => void,
    showToast: (toast: AgTechToast) => void,
    showWarnings: (warnings: AgTechMessage[]) => void,
    removeToast: (toastID: string) => void
}

export const DefaultAgTechToastManager: AgTechToastManager = {
    renderedToasts: [],
    show: (rendering: AgTechRendering) => {},
    showToast: (toast: AgTechToast) => {},
    showMessage: (message: AgTechMessage) => {},
    showWarning: () => {
        console.warn('Toasted warning with default toast manager');
        return false;
    },
    showWarnings: (warnings: AgTechMessage[]) => {},
    showError: (error: AgTechMessage) => {
        console.warn('Toasted error with default toast manager');
        return false;
    },
    showSuccess: () => {
        console.warn('Toasted success with default toast manager');
        return true;
    },
    removeToast: (toastID: string) => {}
}

export function useToasts(): AgTechToastManager {
    let [toasts, setToasts] = useState<AgTechToastRendering[]>([]);
    let toastsRef = useRef<AgTechToastRendering[]>([]);

    const updateToasts = (toasts: AgTechToastRendering[]) => {
        toastsRef.current = [...toasts];
        setToasts(toastsRef.current);
    }

    let showToast = (toast: AgTechToast) => {
        let newToast = getStandardWebToast(toast);
        updateToasts([...toastsRef.current, newToast]);
    }

    let showToasts = (toastsToShow: AgTechToast[]) => {
        let newToasts = toastsToShow.map<AgTechToastRendering>(toast => getStandardWebToast(toast));
        updateToasts([...toastsRef.current, ...newToasts]);
    }

    return {
        renderedToasts: toasts,
        show: rendering => setToasts([...toasts, {
            id: uniqueId(),
            toast: rendering
        }]),
        showToast: toast => showToast(toast),
        showMessage: (message: AgTechMessage) => showToast({
            ...message,
            type: AgTechToastType.Info,
            icon: 'info'
        }),
        showWarning: (message: AgTechMessage | string) => {
            let messageProps = isAgTechMessage(message) ? {
                ...message
            } : {
                header: message
            };
            
            showToast({
                ...messageProps,
                type: AgTechToastType.Warning,
                icon: 'exclamation'
            });

            return false;
        },
        showWarnings: (warnings: AgTechMessage[]) => showToasts(warnings.map(message => ({
            ...message,
            type: AgTechToastType.Warning,
            icon: 'exclamation'
        }))),
        showError: (message: AgTechMessage) => {
            logEvent('ShowErrorToast', {
                message: message
            });

            if (isDev()) {
                showToast({
                    ...message,
                    type: AgTechToastType.Error,
                    icon: 'exclamation'
                });
            }
            else {
                showToast({
                    header: 'An error occurred',
                    details: 'If the error persists, please restart the application.',
                    type: AgTechToastType.Error,
                    icon: 'exclamation'
                });
            }

            return false;
        },
        showSuccess: message => {
            showToast({
                ...message,
                type: AgTechToastType.Success,
                icon: 'check'
            });

            return true;
        },
        removeToast: toastID => {
            toastsRef.current = toastsRef.current.filter(t => t.id !== toastID);

            // Only update state if all toasts have been removed, as auto-fading toasts
            // can cause threading issues if state is updated with each removal
            if (toastsRef.current.length === 0) {
                updateToasts([]);
            }
        }
    }
}

const getStandardWebToast = (toast: AgTechToast): AgTechToastRendering => {
    return {
        id: uniqueId(),
        toast: {
            rendering: () => (
                <AgTechStandardToast
                    type={toast.type ?? AgTechToastType.Info}
                    toast={{
                        header: toast.header,
                        detail: toast.details,
                        icon: toast.icon
                    }}
                />
            )
        }
    }
}

export default AgTechToastSurface;